import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { supabaseClient } from "../lib/supabaseClient";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [clients, setClients] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    specialty: "",
    city: "",
    country: "",
  });

  const loadClients = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/clients`, {
        headers: { Authorization: `Bearer ${t || token}` },
      });
      setClients(res.data?.clients || []);
    } catch (e) {
      toast.error("Could not load clients");
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async (t) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/reports`, {
        headers: { Authorization: `Bearer ${t || token}` },
      });
      setReports(res.data || null);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!supabaseClient) {
      setChecking(false);
      return;
    }
    supabaseClient.auth.getSession().then(({ data }) => {
      const t = data.session?.access_token || null;
      setToken(t);
      if (!t) {
        setChecking(false);
        return;
      }
      axios
        .get(`${BACKEND_URL}/api/admin/is-admin`, {
          headers: { Authorization: `Bearer ${t}` },
        })
        .then((r) => {
          const ok = !!r.data?.is_admin;
          setIsAdmin(ok);
          setChecking(false);
          if (ok) {
            loadClients(t);
            loadReports(t);
          }
        })
        .catch(() => setChecking(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createClient = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, login email and password are required.");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/clients`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLastCreated({ email: form.email, password: form.password, name: form.name });
      toast.success(`Created login for ${form.name}`);
      setForm({ name: "", email: "", password: "", specialty: "", city: "", country: "" });
      loadClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create client");
    } finally {
      setCreating(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-[#5C7063]">Loading…</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 text-center">
        <p className="text-[#5C7063]">
          You do not have admin access with this login. Sign in with your admin email.
        </p>
      </div>
    );
  }

  const inputCls =
    "rounded-xl border border-[#2C5F44]/20 px-3 py-2 text-sm w-full";

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="text-2xl font-bold text-[#1C2A21]">Admin — clients</h1>
          <Button variant="outline" onClick={() => loadClients()}>
            Refresh
          </Button>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-md border border-[#2C5F44]/10 mb-6">
          <h2 className="text-lg font-bold text-[#1C2A21] mb-3">Add a client</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="admin-name" />
            <input placeholder="Login email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="admin-email" />
            <input placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="admin-password" />
            <input placeholder="Specialty (optional)" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className={inputCls} />
            <input placeholder="City (optional)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
            <input placeholder="Country (optional)" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inputCls} />
          </div>
          <Button className="mt-3" onClick={createClient} disabled={creating} data-testid="admin-create">
            {creating ? "Creating…" : "Create client login"}
          </Button>
          {lastCreated && (
            <div className="mt-3 text-sm bg-[#2C5F44]/5 rounded-xl p-3">
              <p className="font-semibold text-[#1C2A21]">Share these with {lastCreated.name}:</p>
              <p className="text-[#1C2A21]">Login email: {lastCreated.email}</p>
              <p className="text-[#1C2A21]">Password: {lastCreated.password}</p>
              <p className="text-xs text-[#5C7063] mt-1">They sign in, then record their avatar and give consent.</p>
            </div>
          )}
        </div>

        {reports && (
          <div className="bg-white rounded-2xl p-5 shadow-md border border-[#2C5F44]/10 mb-6" data-testid="admin-reports">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h2 className="text-lg font-bold text-[#1C2A21]">Reports</h2>
              <Button variant="outline" onClick={() => loadReports()}>Refresh</Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-[#2C5F44]/10 p-3">
                <p className="text-xs text-[#5C7063]">Credits remaining</p>
                <p className="text-2xl font-bold text-[#1C2A21]">{reports.credits_remaining ?? "—"}</p>
                {reports.usd_remaining != null && (
                  <p className="text-xs text-[#5C7063]">≈ ${reports.usd_remaining} left</p>
                )}
              </div>
              <div className="rounded-xl border border-[#2C5F44]/10 p-3">
                <p className="text-xs text-[#5C7063]">Clients</p>
                <p className="text-2xl font-bold text-[#1C2A21]">{reports.total_clients}</p>
                <p className="text-xs text-[#5C7063]">{reports.avatars_ready} avatar ready</p>
              </div>
              <div className="rounded-xl border border-[#2C5F44]/10 p-3">
                <p className="text-xs text-[#5C7063]">Total videos</p>
                <p className="text-2xl font-bold text-[#1C2A21]">{reports.total_videos}</p>
              </div>
              <div className="rounded-xl border border-[#2C5F44]/10 p-3">
                <p className="text-xs text-[#5C7063]">This month</p>
                <p className="text-2xl font-bold text-[#1C2A21]">{reports.videos_this_month}</p>
                <p className="text-xs text-[#5C7063]">{reports.this_month}</p>
              </div>
            </div>
            {reports.per_client && reports.per_client.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-[#1C2A21] mb-2">Videos per client</p>
                <div className="flex flex-col">
                  {reports.per_client.map((c) => (
                    <div key={c.doctor_id} className="flex items-center justify-between text-sm border-b border-[#2C5F44]/5 py-1.5">
                      <span className="text-[#1C2A21]">{c.name}</span>
                      <span className="text-[#5C7063]">this month <span className="font-semibold text-[#1C2A21]">{c.this_month}</span> · total <span className="font-semibold text-[#1C2A21]">{c.total_videos}</span></span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#5C7063] mt-2">Per‑video credit estimates show in each client's Library.</p>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl p-5 shadow-md border border-[#2C5F44]/10">
          <h2 className="text-lg font-bold text-[#1C2A21] mb-3">Onboarded clients</h2>
          {loading ? (
            <p className="text-sm text-[#5C7063]">Loading…</p>
          ) : clients.length === 0 ? (
            <p className="text-sm text-[#5C7063]">No clients yet. Add one above.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {clients.map((c) => (
                <div key={c.doctor_id} className="flex items-center justify-between flex-wrap gap-2 border border-[#2C5F44]/10 rounded-xl px-4 py-3" data-testid="admin-client-row">
                  <div>
                    <p className="text-sm font-semibold text-[#1C2A21]">{c.name}</p>
                    <p className="text-xs text-[#5C7063]">{c.email} · {c.specialty || "—"}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {c.consent_status && (
                      ["approved", "accepted", "completed", "complete", "consented", "success", "verified", "trained", "done"].includes(String(c.consent_status).toLowerCase()) ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-semibold">✓ consent given</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold">consent pending</span>
                      )
                    )}
                    <span className="text-xs px-2 py-1 rounded-full bg-[#2C5F44]/10 text-[#2C5F44]">{c.status}</span>
                    <span className="text-xs text-[#5C7063]">{c.video_count} videos</span>
                    <Button variant="outline" onClick={() => navigate(`/video/${c.doctor_id}`)}>Open studio</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
